import { useRoute } from "#app/composables/router";
import { useRequestHeader } from "#app/composables/ssr";
import { useRequestURL } from "#app/composables/url";

import type { LocaleMorpheus } from "@solvari/translations";

import { toAbsoluteUrl } from "@solvari/utils";

import { internalOrigin } from "~/shared/libs/internalOrigin.ts";
import { hostAndUrlToLocale } from "~/shared/locale/hostLocale.ts";

/*
  This needs to run in injection context
 */
function useHost(): string {
  if (import.meta.server) {
    // `||` like requestHost.ts: empty header falls through to Host.
    let host = useRequestHeader("solvari-host") || useRequestHeader("host");
    if (!host || host === "localhost") {
      host = "solvari.nl";
    }
    return host;
  }
  return useRequestURL().hostname;
}

function useCurrentUrl(): () => string | undefined {
  const host = useHost();
  const route = useRoute();
  return () =>
    // oxlint-disable-next-line typescript/no-unnecessary-condition -- route is undefined on 404 pages despite types
    route?.path ? `https://${host}${route.path}` : undefined;
}

// Captures the host like useCurrentUrl, so the returned function works outside injection context.
function useAbsoluteUrl(): (url: string) => string {
  const host = useHost();
  return (url: string) => `https://${host}${url}`;
}

function toInternalUrl(url: string): string {
  return import.meta.server
    ? toAbsoluteUrl(url, internalOrigin)
    : toAbsoluteUrl(url, `https://${useHost()}`);
}

function useHostLocale(): LocaleMorpheus {
  return hostAndUrlToLocale(useHost(), useRequestURL().pathname);
}

export { toInternalUrl, useAbsoluteUrl, useCurrentUrl, useHost, useHostLocale };
